import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axiosInstance from '../../axiosConfig';

// Keep each request bounded and aggregate pages client-side.
const DEFAULT_LIMIT = 5000;
const MAX_PAGES = 20;

const fetchMpAttributes = async (params) => {
  const limit = Number(params.limit ?? DEFAULT_LIMIT);
  const initialOffset = Number(params.offset ?? 0);

  let offset = initialOffset;
  let pages = 0;
  let count = 0;
  let next = null;
  const results = [];

  do {
    const response = await axiosInstance.get('/mp_attributes/', {
      params: { ...params, limit, offset },
    });
    const payload = response.data || {};
    const pageResults = payload.results || [];

    count = payload.count ?? count;
    next = payload.next ?? null;
    results.push(...pageResults);
    offset += pageResults.length;
    pages += 1;

    if (pageResults.length === 0) break;
  } while (next && pages < MAX_PAGES);

  return { count, results, next, previous: null };
};

export const useGetMpAttributes = (params = {}, options = {}) => {
  const queryParams = useMemo(() => {
    const out = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out;
  }, [params]);

  const { data, error, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['mp-attributes', queryParams],
    queryFn: () => fetchMpAttributes(queryParams),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
  });

  const mpAttributes = data?.results;
  const count = data?.count;
  const next = data?.next;
  const previous = data?.previous;

  return {
    mpAttributes,
    count,
    next,
    previous,
    error,
    isLoading,
    isError,
    isFetching,
    refetch,
  };
};
